import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, ExternalLink, Lock, LogOut, Plus, Send, Share2, Star, Upload, User, X } from 'lucide-react';
import { ref } from 'firebase/storage';
import toast from 'react-hot-toast';
import LoginModal from '../Auth/LoginModal';
import Profile from '../Profile/Profile';
import formIcon from '../../assets/icons/icon.png';
import sheetLogo from '../../assets/images/sheetlogo.png';
import { useAuth } from '../../contexts/AuthContext';
import { storage } from '../../config/firebase';
import { sheetFormService } from '../../services/sheetFormService';
import { sheetService } from '../../services/sheetService';
import { evaluateHeaderConditionalState } from '../../utils/sheetConditionalLogic';
import { resolveRowFormulas } from '../../utils/sheetFormula';
import { resolveHeaderLabel } from '../../utils/sheetHeaderReferences';
import { resolveHeaderOptions } from '../../utils/sheetOptions';
import { applySheetPageMetadata } from '../../utils/sheetShare';
import { uploadFileAndGetUrl } from '../../utils/storageUpload';
import SmartSensorField from '../forms/SmartSensorField';

const DEFAULT_FORM_PRIMARY_COLOR = '#008080';
const DEFAULT_FORM_SECONDARY_COLOR = '#2563eb';
const SHEET_RENDER_FONT_FAMILIES = {
  Inter: '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  Roboto: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  'Open Sans': '"Open Sans", "Helvetica Neue", Arial, sans-serif',
  Montserrat: '"Montserrat", "Avenir Next", "Segoe UI", sans-serif',
  Poppins: '"Poppins", "Avenir Next", "Segoe UI", sans-serif',
  Lato: '"Lato", "Helvetica Neue", Arial, sans-serif',
  Nunito: '"Nunito", "Trebuchet MS", "Segoe UI", sans-serif',
  'Source Sans Pro': '"Source Sans 3", "Source Sans Pro", "Helvetica Neue", Arial, sans-serif',
  Raleway: '"Raleway", "Helvetica Neue", Arial, sans-serif',
  Ubuntu: '"Ubuntu", "Segoe UI", sans-serif',
  'Playfair Display': '"Playfair Display", Georgia, "Times New Roman", serif',
  Merriweather: '"Merriweather", Georgia, serif',
  Georgia: 'Georgia, "Times New Roman", serif',
  'Times New Roman': '"Times New Roman", Times, serif',
  'Crimson Text': '"Crimson Text", Georgia, serif'
};
const SHEET_RENDER_FONT_STYLESHEET_URLS = {
  Inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap',
  Roboto: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap',
  'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&display=swap',
  Montserrat: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap',
  Poppins: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap',
  Lato: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap',
  Nunito: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap',
  'Source Sans Pro': 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700;800;900&display=swap',
  Raleway: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap',
  Ubuntu: 'https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;500;700&display=swap',
  'Playfair Display': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&display=swap',
  Merriweather: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700;900&display=swap',
  'Crimson Text': 'https://fonts.googleapis.com/css2?family=Crimson+Text:wght@400;600;700&display=swap'
};
const getSheetRenderFontFamily = (value) => SHEET_RENDER_FONT_FAMILIES[`${value || ''}`.trim()] || SHEET_RENDER_FONT_FAMILIES.Inter;
const getSheetRenderFontStylesheetUrl = (value) => SHEET_RENDER_FONT_STYLESHEET_URLS[`${value || ''}`.trim()] || SHEET_RENDER_FONT_STYLESHEET_URLS.Inter;
const normalizeThemeColor = (value) => {
  const normalized = `${value || ''}`.trim();
  return /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)
    ? (normalized.startsWith('#') ? normalized : `#${normalized}`)
    : '';
};
const expandHex = (value) => {
  const normalized = normalizeThemeColor(value);
  if (!normalized) return '';
  if (normalized.length === 4) {
    return `#${normalized.slice(1).split('').map((char) => `${char}${char}`).join('')}`;
  }
  return normalized;
};
const hexToRgba = (value, alpha) => {
  const expanded = expandHex(value);
  if (!expanded) return '';
  const hex = expanded.slice(1);
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};
const buildFormTheme = (form) => {
  const primary = expandHex(form?.primaryColor) || DEFAULT_FORM_PRIMARY_COLOR;
  const secondary = expandHex(form?.secondaryColor) || DEFAULT_FORM_SECONDARY_COLOR;
  return {
    primary,
    secondary,
    primarySoft: hexToRgba(primary, 0.1),
    primaryBorder: hexToRgba(primary, 0.24),
    primaryStrong: hexToRgba(primary, 0.18),
    secondarySoft: hexToRgba(secondary, 0.1)
  };
};
const normalizeAvailabilityDateTime = (value) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return null;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};
const getFormAvailabilityState = (formData = {}, now = new Date()) => {
  const startAt = normalizeAvailabilityDateTime(formData?.availabilityStartAt);
  const endAt = normalizeAvailabilityDateTime(formData?.availabilityEndAt);
  if (startAt && now < startAt) return 'not_started';
  if (endAt && now > endAt) return 'ended';
  return 'available';
};
const formatAvailabilityDateTime = (value) => {
  const date = normalizeAvailabilityDateTime(value);
  if (!date) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getInputTypeForHeader = (headerType) => {
  switch (`${headerType || ''}`.trim()) {
    case 'number':
    case 'currency':
    case 'percent':
      return 'number';
    case 'email':
      return 'email';
    case 'telephone':
      return 'tel';
    case 'hyperlink':
    case 'youtube':
    case 'document':
    case 'image':
      return 'url';
    case 'date':
      return 'date';
    case 'time':
      return 'time';
    case 'datetime':
      return 'datetime-local';
    default:
      return 'text';
  }
};

const getInputModeForHeader = (headerType) => {
  switch (`${headerType || ''}`.trim()) {
    case 'number':
    case 'currency':
    case 'percent':
    case 'rating':
      return 'decimal';
    case 'telephone':
      return 'tel';
    case 'email':
      return 'email';
    default:
      return undefined;
  }
};

const normalizeFormFieldWidth = (value) => {
  const normalized = `${value || ''}`.trim();
  return ['full', 'two-thirds', 'half', 'third'].includes(normalized) ? normalized : 'full';
};
const normalizeSectionDividerLabel = (item = {}) => {
  const label = item.label ?? item.content ?? 'Section';
  const normalizedLabel = `${label}`;
  return normalizedLabel.trim() ? normalizedLabel : 'Section';
};
const canUseSensorInputMode = (fieldType) => (
  ['text', 'email', 'telephone', 'address', 'hyperlink'].includes(`${fieldType || ''}`.trim())
);

const normalizeFormInputMode = (value) => (
  ['default', 'barcode', 'qrcode', 'nfc'].includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'default'
);
const getLegacyInputModeFromFieldType = (fieldType) => (
  ['barcode', 'qrcode', 'nfc'].includes(`${fieldType || ''}`.trim()) ? `${fieldType || ''}`.trim() : 'default'
);
const getSensorFieldType = (inputMode) => {
  switch (`${inputMode || ''}`.trim()) {
    case 'barcode':
      return 'barcodeInput';
    case 'qrcode':
      return 'qrCodeInput';
    case 'nfc':
      return 'nfcInput';
    default:
      return '';
  }
};

const getFormFieldWidthClass = (width) => {
  switch (normalizeFormFieldWidth(width)) {
    case 'two-thirds':
      return 'md:col-span-4';
    case 'half':
      return 'md:col-span-3';
    case 'third':
      return 'md:col-span-2';
    default:
      return 'md:col-span-6';
  }
};

const normalizeSubmissionMode = (value) => (
  `${value || ''}`.trim() === 'modular' ? 'modular' : 'singleRow'
);

const normalizeMinimumRepeatableRows = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.min(25, Math.round(numeric)));
};
const normalizeRepeatableEntryMode = (value) => (
  `${value || ''}`.trim() === 'allAtOnce' ? 'allAtOnce' : 'stepper'
);
const OPTION_SOURCE_LOAD_TIMEOUT_MS = 5000;
const normalizeTextBlockAlignment = (value) => (
  ['left', 'center', 'right'].includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'left'
);
const normalizeSlideMode = (value) => (
  ['manual', 'auto'].includes(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'manual'
);
const normalizeSlideIntervalSeconds = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 5;
  return Math.max(2, Math.min(30, Math.round(numeric)));
};
const sanitizeRichTextHtml = (html) => {
  const source = `${html || ''}`.trim();
  if (!source) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return source;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
  const root = doc.body.firstElementChild || doc.body;
  const allowedTags = new Set(['p', 'div', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a']);

  const sanitizeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return doc.createTextNode(node.textContent || '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return doc.createDocumentFragment();
    }

    const tag = node.tagName.toLowerCase();
    const fragment = doc.createDocumentFragment();
    const children = Array.from(node.childNodes).map((child) => sanitizeNode(child));

    if (!allowedTags.has(tag)) {
      children.forEach((child) => fragment.appendChild(child));
      return fragment;
    }

    const element = doc.createElement(tag === 'b' ? 'strong' : tag === 'i' ? 'em' : tag);
    if (tag === 'a') {
      const href = `${node.getAttribute('href') || ''}`.trim();
      if (/^https?:\/\//i.test(href)) {
        element.setAttribute('href', href);
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noreferrer');
      }
    }
    if (['p', 'div'].includes(tag)) {
      const textAlign = `${node.style?.textAlign || node.getAttribute('align') || ''}`.trim().toLowerCase();
      if (['left', 'center', 'right'].includes(textAlign)) {
        element.style.textAlign = textAlign;
      }
    }
    children.forEach((child) => element.appendChild(child));
    return element;
  };

  const output = doc.createElement('div');
  Array.from(root.childNodes).forEach((child) => output.appendChild(sanitizeNode(child)));
  return output.innerHTML.trim();
};
const escapeInlineMarkdownHtml = (value) =>
  `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderInlineMarkdown = (value) => {
  let html = escapeInlineMarkdownHtml(value);
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  return html;
};

const MarkdownLabel = ({ children, className = '' }) => {
  const html = useMemo(() => renderInlineMarkdown(children), [children]);
  return (
    <span
      className={`inline [&_a]:font-medium [&_a]:underline [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.92em] ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
const normalizeSlideItems = (slides = []) => (
  (Array.isArray(slides) ? slides : [])
    .map((slide, index) => {
      const url = `${slide?.url || slide?.src || ''}`.trim();
      if (!url) return null;
      return {
        id: `${slide?.id || `slide_${index + 1}`}`.trim() || `slide_${index + 1}`,
        url,
        alt: `${slide?.alt || slide?.caption || ''}`.trim()
      };
    })
    .filter(Boolean)
);
const formatRowSelectorValue = (header, value, rowValues = {}) => {
  if (!header) return '';
  if (header.type === 'profile') {
    const labelHeaderId = `${header?.profileConfig?.labelHeaderId || ''}`.trim();
    const profileSource = value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : rowValues;
    const labelValue = labelHeaderId
      ? `${profileSource?.[labelHeaderId] ?? rowValues?.[labelHeaderId] ?? ''}`.trim()
      : '';
    if (labelValue) return labelValue;
    if (typeof value === 'string' || typeof value === 'number') {
      return `${value}`.trim();
    }
    return `${rowValues?.[header.id] ?? ''}`.trim();
  }
  if (header.type === 'checkbox') {
    return value === true ? 'Yes' : value === false ? 'No' : '';
  }
  if (header.type === 'multiselect' || header.type === 'list') {
    return Array.isArray(value) ? value.map((item) => `${item || ''}`.trim()).filter(Boolean).join(', ') : '';
  }
  return `${value ?? ''}`.trim();
};
const getRowSelectorDisplayData = (header, rowValues = {}) => {
  const label = formatRowSelectorValue(header, rowValues?.[header?.id], rowValues);
  if (`${header?.type || ''}`.trim() !== 'profile') {
    return {
      label,
      imageUrl: ''
    };
  }

  const imageHeaderId = `${header?.profileConfig?.imageHeaderId || ''}`.trim();
  return {
    label,
    imageUrl: `${rowValues?.[imageHeaderId] ?? ''}`.trim()
  };
};
const buildActionPrefilledDraft = (form, baseDraft, prefillValues = {}) => {
  const sourceDraft = baseDraft && typeof baseDraft === 'object'
    ? JSON.parse(JSON.stringify(baseDraft))
    : sheetFormService.createInitialValues(form);
  const nextDraft = sourceDraft && typeof sourceDraft === 'object' ? sourceDraft : {};
  const nextValues = prefillValues && typeof prefillValues === 'object' ? prefillValues : {};
  const sharedFieldIds = new Set((Array.isArray(form?.fields) ? form.fields : []).map((field) => field.id));
  const repeatableFieldIds = new Set((Array.isArray(form?.repeatableFields) ? form.repeatableFields : []).map((field) => field.id));

  Object.entries(nextValues).forEach(([fieldId, value]) => {
    if (!fieldId) return;
    if (normalizeSubmissionMode(form?.submissionMode) !== 'modular') {
      nextDraft[fieldId] = value;
      return;
    }

    if (sharedFieldIds.has(fieldId)) {
      nextDraft.shared = {
        ...getSharedDraftValues(nextDraft),
        [fieldId]: value
      };
      return;
    }

    if (repeatableFieldIds.has(fieldId)) {
      const currentRows = Array.isArray(nextDraft.rows) && nextDraft.rows.length
        ? nextDraft.rows
        : [createEmptyRepeatableRow(form)];
      nextDraft.rows = currentRows.map((row, index) => (
        index === 0 ? { ...(row || {}), [fieldId]: value } : row
      ));
    }
  });

  return nextDraft;
};
const normalizeFormPrefillSelectorConfig = (config = {}) => ({
  enabled: config?.enabled === true,
  sourceSheetId: `${config?.sourceSheetId || ''}`.trim(),
  actionId: `${config?.actionId || ''}`.trim()
});
const mapSourceRowToPrefillValues = (fieldMappings = [], sourceValues = {}) => (
  (Array.isArray(fieldMappings) ? fieldMappings : []).reduce((acc, fieldMapping) => {
    const targetHeaderId = `${fieldMapping?.targetHeaderId || ''}`.trim();
    const valueMode = `${fieldMapping?.valueMode || ''}`.trim() === 'static'
      ? 'static'
      : (`${fieldMapping?.sourceHeaderId || ''}`.trim() ? 'sourceHeader' : 'static');
    const sourceHeaderId = `${fieldMapping?.sourceHeaderId || ''}`.trim();
    if (!targetHeaderId) return acc;
    acc[targetHeaderId] = valueMode === 'static'
      ? (fieldMapping?.value ?? '')
      : (sourceValues?.[sourceHeaderId] ?? '');
    return acc;
  }, {})
);

const FormRichTextBlock = ({ item, theme }) => {
  const html = useMemo(() => sanitizeRichTextHtml(item?.contentHtml || item?.content || ''), [item]);
  if (!html) return null;
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  }[normalizeTextBlockAlignment(item?.alignment)];

  return (
    <div
      className={`prose prose-slate max-w-none text-sm leading-6 text-slate-700 [&_a]:underline [&_ol]:pl-5 [&_p]:mb-3 [&_ul]:pl-5 ${alignClass}`}
      style={{ '--tw-prose-links': theme?.primary || DEFAULT_FORM_PRIMARY_COLOR }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const FormImageSlide = ({ item, theme }) => {
  const slides = useMemo(() => normalizeSlideItems(item?.slides), [item]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const slideMode = normalizeSlideMode(item?.slideMode);
  const autoAdvanceSeconds = normalizeSlideIntervalSeconds(item?.autoAdvanceSeconds);

  useEffect(() => {
    setActiveSlideIndex(0);
  }, [item?.id, slides.length]);

  useEffect(() => {
    if (slideMode !== 'auto' || slides.length <= 1) return undefined;
    const intervalId = window.setInterval(() => {
      setActiveSlideIndex((current) => (current + 1) % slides.length);
    }, autoAdvanceSeconds * 1000);
    return () => window.clearInterval(intervalId);
  }, [autoAdvanceSeconds, slideMode, slides.length]);

  if (!slides.length) return null;

  const activeSlide = slides[Math.min(activeSlideIndex, slides.length - 1)] || slides[0];

  return (
    <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-slate-50 shadow-sm">
      <div className="relative">
        <img
          src={activeSlide.url}
          alt={activeSlide.alt || 'Slide'}
          className="max-h-72 w-full object-cover sm:max-h-80"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent px-4 pb-4 pt-12">
          {activeSlide.alt ? (
            <p className="text-sm font-medium text-white">{activeSlide.alt}</p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setActiveSlideIndex(slideIndex)}
              className={`h-2.5 rounded-full transition ${slideIndex === activeSlideIndex ? 'w-8' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`}
              style={slideIndex === activeSlideIndex ? { backgroundColor: theme?.primary || DEFAULT_FORM_PRIMARY_COLOR } : undefined}
              aria-label={`Show slide ${slideIndex + 1}`}
            />
          ))}
        </div>

        {slides.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveSlideIndex((current) => (current - 1 + slides.length) % slides.length)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300"
              aria-label="Previous slide"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setActiveSlideIndex((current) => (current + 1) % slides.length)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300"
              aria-label="Next slide"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const buildFormLayoutItems = (form) => {
  const fields = Array.isArray(form?.fields) ? form.fields : [];
  const fieldMap = new Map(fields.map((field) => [`${field?.id || ''}`.trim(), field]));
  const rawLayout = Array.isArray(form?.formLayout) && form.formLayout.length
    ? form.formLayout
    : fields.map((field) => ({
        id: `field_${field.id}`,
        type: 'field',
        headerId: field.id,
        width: field.formWidth || 'full'
      }));

  const items = [];
  const seenFieldIds = new Set();

  rawLayout.forEach((item, index) => {
    const type = `${item?.type || ''}`.trim();

    if (type === 'field') {
      const headerId = `${item?.headerId || item?.fieldId || ''}`.trim();
      const field = fieldMap.get(headerId);
      if (!field || seenFieldIds.has(headerId)) return;
      seenFieldIds.add(headerId);
      items.push({
        id: `${item?.id || `field_${headerId}`}`.trim() || `field_${headerId}`,
        type: 'field',
        width: normalizeFormFieldWidth(item?.width || field.formWidth),
        field
      });
      return;
    }

    if (type === 'sectionDivider') {
      items.push({
        id: `${item?.id || `section_${index + 1}`}`.trim() || `section_${index + 1}`,
        type: 'sectionDivider',
        label: normalizeSectionDividerLabel(item)
      });
      return;
    }

    if (type === 'textBlock') {
      items.push({
        id: `${item?.id || `text_block_${index + 1}`}`.trim() || `text_block_${index + 1}`,
        type: 'textBlock',
        contentHtml: `${item?.contentHtml || item?.content || ''}`,
        alignment: normalizeTextBlockAlignment(item?.alignment)
      });
      return;
    }

    if (type === 'imageSlide') {
      items.push({
        id: `${item?.id || `image_slide_${index + 1}`}`.trim() || `image_slide_${index + 1}`,
        type: 'imageSlide',
        slides: normalizeSlideItems(item?.slides),
        slideMode: normalizeSlideMode(item?.slideMode),
        autoAdvanceSeconds: normalizeSlideIntervalSeconds(item?.autoAdvanceSeconds)
      });
      return;
    }

    if (type === 'pageBreak') {
      items.push({
        id: `${item?.id || `page_break_${index + 1}`}`.trim() || `page_break_${index + 1}`,
        type: 'pageBreak'
      });
    }
  });

  if (!items.some((item) => item.type === 'field')) {
    return fields.map((field) => ({
      id: `field_${field.id}`,
      type: 'field',
      width: normalizeFormFieldWidth(field.formWidth),
      field
    }));
  }

  return items;
};

const buildRepeatableLayoutItems = (form) => {
  const fields = Array.isArray(form?.repeatableFields) ? form.repeatableFields : [];
  const fieldMap = new Map(fields.map((field) => [`${field?.id || ''}`.trim(), field]));
  const rawLayout = Array.isArray(form?.repeatableLayout) && form.repeatableLayout.length
    ? form.repeatableLayout
    : fields.map((field) => ({
        id: `field_${field.id}`,
        type: 'field',
        headerId: field.id,
        width: field.formWidth || 'full'
      }));

  const items = [];
  const seenFieldIds = new Set();

  rawLayout.forEach((item, index) => {
    const type = `${item?.type || ''}`.trim();

    if (type === 'field') {
      const headerId = `${item?.headerId || item?.fieldId || ''}`.trim();
      const field = fieldMap.get(headerId);
      if (!field || seenFieldIds.has(headerId)) return;
      seenFieldIds.add(headerId);
      items.push({
        id: `${item?.id || `repeatable_field_${headerId}`}`.trim() || `repeatable_field_${headerId}`,
        type: 'field',
        width: normalizeFormFieldWidth(item?.width || field.formWidth),
        field
      });
      return;
    }

    if (type === 'sectionDivider') {
      items.push({
        id: `${item?.id || `repeatable_section_${index + 1}`}`.trim() || `repeatable_section_${index + 1}`,
        type: 'sectionDivider',
        label: normalizeSectionDividerLabel(item)
      });
      return;
    }

    if (type === 'textBlock') {
      items.push({
        id: `${item?.id || `repeatable_text_block_${index + 1}`}`.trim() || `repeatable_text_block_${index + 1}`,
        type: 'textBlock',
        contentHtml: `${item?.contentHtml || item?.content || ''}`,
        alignment: normalizeTextBlockAlignment(item?.alignment)
      });
      return;
    }

    if (type === 'imageSlide') {
      items.push({
        id: `${item?.id || `repeatable_image_slide_${index + 1}`}`.trim() || `repeatable_image_slide_${index + 1}`,
        type: 'imageSlide',
        slides: normalizeSlideItems(item?.slides),
        slideMode: normalizeSlideMode(item?.slideMode),
        autoAdvanceSeconds: normalizeSlideIntervalSeconds(item?.autoAdvanceSeconds)
      });
      return;
    }

    if (type === 'pageBreak') {
      items.push({
        id: `${item?.id || `repeatable_page_break_${index + 1}`}`.trim() || `repeatable_page_break_${index + 1}`,
        type: 'pageBreak'
      });
    }
  });

  if (!items.some((item) => item.type === 'field')) {
    return fields.map((field) => ({
      id: `field_${field.id}`,
      type: 'field',
      width: normalizeFormFieldWidth(field.formWidth),
      field
    }));
  }

  return items;
};

const getSharedDraftValues = (draft) => (
  draft && typeof draft === 'object' && !Array.isArray(draft) && draft.shared && typeof draft.shared === 'object'
    ? draft.shared
    : {}
);

const createEmptyRepeatableRow = (form) => {
  const repeatableFields = Array.isArray(form?.repeatableFields) ? form.repeatableFields : [];
  return repeatableFields.reduce((acc, field) => {
    acc[field.id] = getInitialValueForField(field);
    return acc;
  }, {});
};

const getRepeatableDraftRows = (draft, form) => {
  if (draft && typeof draft === 'object' && !Array.isArray(draft) && Array.isArray(draft.rows) && draft.rows.length) {
    return draft.rows;
  }

  const minimumRows = normalizeMinimumRepeatableRows(form?.minimumRepeatableRows);
  return Array.from({ length: minimumRows }).map(() => createEmptyRepeatableRow(form));
};

const getInitialValueForField = (field) => {
  if (!field || field.defaultValueMode === 'none') {
    if (field?.type === 'multiselect' || field?.type === 'list') return [];
    if (field?.type === 'checkbox') return false;
    return '';
  }

  if (field.type === 'checkbox') return Boolean(field.defaultValue);
  if (field.type === 'multiselect' || field.type === 'list') {
    return Array.isArray(field.defaultValue) ? field.defaultValue : [];
  }

  return field.defaultValue ?? '';
};

const isFieldEmpty = (field, value) => {
  if (field?.type === 'checkbox') return value !== true;
  if (field?.type === 'multiselect' || field?.type === 'list') return !Array.isArray(value) || value.length === 0;
  return `${value ?? ''}`.trim() === '';
};

const parseMultiValueInput = (rawValue) => (
  Array.from(new Set(
    `${rawValue || ''}`
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
  ))
);

const getRogButtonTone = (value) => {
  switch (`${value || ''}`.trim().toLowerCase()) {
    case 'red':
      return {
        solid: 'border-rose-500 bg-rose-500 text-white',
        soft: 'border-rose-200 bg-rose-50 text-rose-700'
      };
    case 'orange':
      return {
        solid: 'border-orange-500 bg-orange-500 text-white',
        soft: 'border-orange-200 bg-orange-50 text-orange-700'
      };
    case 'green':
      return {
        solid: 'border-emerald-500 bg-emerald-500 text-white',
        soft: 'border-emerald-200 bg-emerald-50 text-emerald-700'
      };
    default:
      return null;
  }
};

const getChoiceButtonPresentation = ({ selected, headerType = '', option = '', theme }) => {
  if (`${headerType || ''}`.trim() === 'rog') {
    const tone = getRogButtonTone(option);
    if (tone) {
      return {
        className: [
          'rounded-xl border px-4 py-2 text-sm font-semibold shadow-md transition',
          selected ? tone.solid : tone.soft
        ].join(' ')
      };
    }

    return {
      className: 'rounded-xl border px-4 py-2 text-sm font-semibold shadow-md transition',
      style: selected
        ? {
            borderColor: theme?.primary || DEFAULT_FORM_PRIMARY_COLOR,
            backgroundColor: theme?.primary || DEFAULT_FORM_PRIMARY_COLOR,
            color: '#ffffff'
          }
        : {
            borderColor: '#e2e8f0',
            backgroundColor: '#ffffff',
            color: '#334155'
          }
    };
  }

  return {
    className: 'rounded-xl border px-4 py-2 text-sm font-semibold shadow-md transition',
    style: selected
      ? {
          borderColor: theme?.primary || DEFAULT_FORM_PRIMARY_COLOR,
          backgroundColor: theme?.primary || DEFAULT_FORM_PRIMARY_COLOR,
          color: '#f8fafc'
        }
      : {
          borderColor: '#f1f5f9',
          backgroundColor: '#f6f5f5',
          color: theme?.primary || DEFAULT_FORM_PRIMARY_COLOR
        }
  };
};

const getUserDisplayName = (userProfile, user) => (
  userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'User'
);

const getResolvedFormContextValues = (form, values = {}, referenceSheetsById = {}) => {
  const allHeaders = Array.isArray(form?.sheetHeaders) ? form.sheetHeaders : [];
  if (!allHeaders.length) {
    return values && typeof values === 'object' ? values : {};
  }

  return resolveRowFormulas(
    allHeaders,
    values && typeof values === 'object' ? values : {},
    referenceSheetsById
  );
};

const FormShell = ({ children, form, theme, user, userProfile, onSignIn, onShare }) => {
  const { signOut } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const userMenuRef = useRef(null);
  const showProfileSection = Boolean(form) && form.accessMode !== 'public';
  const showShellHeader = form?.showShellHeader !== false;
  const userDisplayName = getUserDisplayName(userProfile, user);
  const userAvatar = userProfile?.photoURL || user?.photoURL || '';
  const renderFontFamily = getSheetRenderFontFamily(form?.renderFont);
  const renderFontStylesheetUrl = getSheetRenderFontStylesheetUrl(form?.renderFont);
  const hasResolvedForm = Boolean(form && Object.keys(form).length);
  const customLogoUrl = `${form?.logoUrl || ''}`.trim();
  const brandLogo = !hasResolvedForm ? '' : (customLogoUrl || formIcon);
  const brandLabel = `${form?.title || form?.linkedSheetTitle || 'ParrotSheets'}`.trim() || 'ParrotSheets';
  const userEmail = `${user?.email || ''}`.trim();
  const userInitial = userDisplayName.slice(0, 1).toUpperCase() || 'U';

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isUserMenuOpen]);

  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [user?.uid]);

  useEffect(() => {
    if (!renderFontStylesheetUrl || typeof document === 'undefined') return undefined;

    const linkId = `sheet-render-font-${`${form?.renderFont || 'Inter'}`.trim().replace(/\s+/g, '-').toLowerCase()}`;
    let linkElement = document.getElementById(linkId);
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.id = linkId;
      linkElement.rel = 'stylesheet';
      linkElement.href = renderFontStylesheetUrl;
      document.head.appendChild(linkElement);
    }

    return undefined;
  }, [form?.renderFont, renderFontStylesheetUrl]);

  const openProfileModal = () => {
    setIsUserMenuOpen(false);
    setIsProfileModalOpen(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error(error?.message || 'Failed to sign out.');
    }
  };

  return (
    <div
      className="min-h-screen bg-[linear-gradient(180deg,#eef7f2_0%,#f8fbf7_48%,#ffffff_100%)] px-2 py-2 sm:px-6 sm:py-3 [&_button]:font-inherit [&_input]:font-inherit [&_select]:font-inherit [&_textarea]:font-inherit"
      style={{
        fontFamily: renderFontFamily,
        backgroundImage: `linear-gradient(180deg, ${theme?.secondarySoft || hexToRgba(DEFAULT_FORM_SECONDARY_COLOR, 0.08)} 0%, ${theme?.primarySoft || hexToRgba(DEFAULT_FORM_PRIMARY_COLOR, 0.08)} 42%, #ffffff 100%)`
      }}
    >
      <div className="mx-auto max-w-2xl">
        {showShellHeader && (
          <div
            className="sticky top-2 z-20 mb-2 flex items-center justify-between gap-4 rounded-[12px] px-4 py-2 shadow-sm backdrop-blur sm:top-4"
            style={{ backgroundColor: 'rgba(255,255,255,0)', border: `0px solid ${theme?.primaryBorder || hexToRgba(DEFAULT_FORM_PRIMARY_COLOR, 0.24)}` }}
          >
            {brandLogo ? (
              <img src={brandLogo} alt={brandLabel} className="h-16 w-auto max-w-[180px] rounded-xl object-contain" />
            ) : (
              <div className="h-14 w-[56px] shrink-0" aria-hidden="true" />
            )}

            <div className="flex items-center gap-3">
              {form && (
                <button
                  type="button"
                  onClick={onShare}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-[#f6f5f5] px-4 py-2 text-sm font-semibold shadow-md text-slate-700 transition hover:border-slate-300"
                  style={{ borderColor: theme?.primaryBorder, color: theme?.primary }}
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              )}
              {showProfileSection && (
                user ? (
                  <div className="relative" ref={userMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsUserMenuOpen((prev) => !prev)}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md transition hover:border-slate-300 hover:bg-slate-50"
                      aria-label="Open user menu"
                      aria-expanded={isUserMenuOpen}
                    >
                      {userAvatar ? (
                        <img
                          src={userAvatar}
                          alt={userDisplayName}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold uppercase"
                          style={{ backgroundColor: theme?.primarySoft, color: theme?.primary }}
                        >
                          {userInitial}
                        </div>
                      )}
                    </button>

                    {isUserMenuOpen && (
                      <div className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-xl">
                        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-4">
                          {userAvatar ? (
                            <img
                              src={userAvatar}
                              alt={userDisplayName}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <div
                              className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold uppercase"
                              style={{ backgroundColor: theme?.primarySoft, color: theme?.primary }}
                            >
                              {userInitial}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{userDisplayName}</p>
                            <p className="truncate text-xs text-slate-500">{userEmail || 'Signed in'}</p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={openProfileModal}
                          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                          <User className="h-4 w-4" />
                          Profile
                        </button>

                        <button
                          type="button"
                          onClick={handleSignOut}
                          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-rose-600 transition hover:bg-rose-50"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onSignIn}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                  >
                    <Lock className="h-4 w-4" />
                    Sign in
                  </button>
                )
              )}
            </div>
          </div>
        )}

        {children}
      </div>

      {isProfileModalOpen && <Profile isModal onClose={() => setIsProfileModalOpen(false)} />}
    </div>
  );
};

const SheetFormPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showErrorSignIn, setShowErrorSignIn] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [submissionState, setSubmissionState] = useState(null);
  const [uploadingFieldKey, setUploadingFieldKey] = useState('');
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentDetailRowIndex, setCurrentDetailRowIndex] = useState(0);
  const [optionSourceRowsBySheetId, setOptionSourceRowsBySheetId] = useState({});
  const [draftStatus, setDraftStatus] = useState('');
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [multiValueDrafts, setMultiValueDrafts] = useState({});
  const [actionPrefillDraft, setActionPrefillDraft] = useState(null);
  const [actionPrefillFieldMappings, setActionPrefillFieldMappings] = useState([]);
  const [sourceSheetLabelHeader, setSourceSheetLabelHeader] = useState(null);
  const [targetPrefillRows, setTargetPrefillRows] = useState([]);
  const [selectedTargetPrefillRowId, setSelectedTargetPrefillRowId] = useState('');
  const [isTargetPrefillPickerOpen, setIsTargetPrefillPickerOpen] = useState(false);
  const targetPrefillPickerRef = useRef(null);
  const canUsePublicAssetUploads = form?.accessMode === 'public';
  const assetUploadOwnerSegment = canUsePublicAssetUploads ? 'public' : user?.uid;
  const actionContext = useMemo(() => {
    const searchParams = new URLSearchParams(location.search || '');
    return {
      actionId: `${searchParams.get('actionId') || ''}`.trim(),
      sourceSheetId: `${searchParams.get('sourceSheetId') || ''}`.trim()
    };
  }, [location.search]);
  const formPrefillSelectorConfig = useMemo(
    () => normalizeFormPrefillSelectorConfig(form?.prefillSelectorConfig),
    [form?.prefillSelectorConfig]
  );

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = `${form?.title || form?.linkedSheetTitle || 'ParrotSheets Form'}`.trim() || 'ParrotSheets Form';
    const shareText = `${form?.description || ''}`.trim() || `Open ${shareTitle}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      toast.success('Form link copied.');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      toast.error('Unable to share this form right now.');
    }
  };

  useEffect(() => {
    let isActive = true;

    const loadForm = async () => {
      setLoading(true);
      setError('');
      setShowErrorSignIn(false);
      setDraftHydrated(false);
      setDraftStatus('');

      try {
        const nextForm = await sheetFormService.getSheetFormById(id);
        if (!isActive) return;

        if (!nextForm) {
          setForm(null);
          setRequiresSignIn(false);
          setShowErrorSignIn(false);
          setError('This form could not be found.');
          return;
        }

        setForm(nextForm);
        setRequiresSignIn(nextForm.accessMode === 'internal');
        const availabilityState = getFormAvailabilityState(nextForm);
        if (availabilityState === 'not_started') {
          setShowErrorSignIn(false);
          setError(`This form is not available yet${nextForm.availabilityStartAt ? `; it opens ${formatAvailabilityDateTime(nextForm.availabilityStartAt)}.` : '.'}`);
          setDraft(sheetFormService.createInitialValues(nextForm));
          setDraftHydrated(true);
          return;
        }
        if (availabilityState === 'ended') {
          setShowErrorSignIn(false);
          setError(`This form is no longer available${nextForm.availabilityEndAt ? `; it closed ${formatAvailabilityDateTime(nextForm.availabilityEndAt)}.` : '.'}`);
          setDraft(sheetFormService.createInitialValues(nextForm));
          setDraftHydrated(true);
          return;
        }
        const initialValues = sheetFormService.createInitialValues(nextForm);
        let nextDraft = initialValues;
        let nextPageIndex = 0;
        let nextDetailRowIndex = 0;

        if (!actionContext.actionId && nextForm.allowDrafts === true && user?.uid) {
          const savedDraft = await sheetFormService.getSheetFormDraft(id, user.uid).catch(() => null);
          if (savedDraft?.values && typeof savedDraft.values === 'object') {
            nextDraft = savedDraft.values;
            nextPageIndex = Math.max(0, Number(savedDraft?.navigationState?.currentPageIndex) || 0);
            nextDetailRowIndex = Math.max(0, Number(savedDraft?.navigationState?.currentDetailRowIndex) || 0);
            toast.success('Draft restored.');
          }
        }

        setDraft(nextDraft);
        setCurrentPageIndex(nextPageIndex);
        setCurrentDetailRowIndex(nextDetailRowIndex);
        setDraftHydrated(true);
      } catch (err) {
        if (!isActive) return;
        const message = `${err?.message || ''}`.toLowerCase();
        if (!user && (message.includes('permission') || message.includes('missing or insufficient permissions'))) {
          setRequiresSignIn(true);
          setShowErrorSignIn(true);
          setError('This form requires sign in before it can be opened.');
        } else {
          setRequiresSignIn(false);
          setShowErrorSignIn(false);
          setError(err?.message || 'Failed to load the form.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    if (!authLoading) {
      loadForm();
    }

    return () => {
      isActive = false;
    };
  }, [actionContext.actionId, authLoading, id, user]);

  const submissionMode = useMemo(() => normalizeSubmissionMode(form?.submissionMode), [form?.submissionMode]);
  const repeatableFields = useMemo(() => (
    submissionMode === 'modular' && Array.isArray(form?.repeatableFields)
      ? form.repeatableFields
      : []
  ), [form?.repeatableFields, submissionMode]);
  const repeatableEntryMode = useMemo(
    () => normalizeRepeatableEntryMode(form?.repeatableEntryMode),
    [form?.repeatableEntryMode]
  );
  const sharedDraftValues = useMemo(
    () => (submissionMode === 'modular' ? getSharedDraftValues(draft) : draft),
    [draft, submissionMode]
  );
  const repeatableDraftRows = useMemo(
    () => (submissionMode === 'modular' ? getRepeatableDraftRows(draft, form) : []),
    [draft, form, submissionMode]
  );
  const resolvedSharedDraftValues = useMemo(
    () => getResolvedFormContextValues(form, sharedDraftValues, optionSourceRowsBySheetId),
    [form, optionSourceRowsBySheetId, sharedDraftValues]
  );
  const formReferenceHeaders = useMemo(
    () => (Array.isArray(form?.sheetHeaders) ? form.sheetHeaders : [
      ...(Array.isArray(form?.fields) ? form.fields : []),
      ...(Array.isArray(form?.repeatableFields) ? form.repeatableFields : [])
    ]),
    [form]
  );
  const visibleFields = useMemo(
    () => (Array.isArray(form?.fields) ? form.fields
      .filter((field) => !evaluateHeaderConditionalState(field, resolvedSharedDraftValues, formReferenceHeaders).hidden)
      .map((field) => ({
        ...field,
        label: resolveHeaderLabel(field, formReferenceHeaders, resolvedSharedDraftValues)
      })) : []),
    [form, formReferenceHeaders, resolvedSharedDraftValues]
  );
  const layoutItems = useMemo(() => buildFormLayoutItems(form), [form]);
  const repeatableLayoutItems = useMemo(() => buildRepeatableLayoutItems(form), [form]);
  const visibleLayoutItems = useMemo(
    () => layoutItems
      .filter((item) => item.type !== 'field' || !evaluateHeaderConditionalState(item.field, resolvedSharedDraftValues, formReferenceHeaders).hidden)
      .map((item) => item.type === 'field'
        ? {
            ...item,
            field: {
              ...item.field,
              label: resolveHeaderLabel(item.field, formReferenceHeaders, resolvedSharedDraftValues)
            }
          }
        : item),
    [formReferenceHeaders, layoutItems, resolvedSharedDraftValues]
  );
  const formPages = useMemo(() => {
    const pages = [];
    let currentItems = [];

    visibleLayoutItems.forEach((item) => {
      if (item.type === 'pageBreak') {
        if (currentItems.length > 0) {
          pages.push(currentItems);
          currentItems = [];
        }
        return;
      }

      currentItems.push(item);
    });

    if (currentItems.length > 0) {
      pages.push(currentItems);
    }

    return pages.length > 0 ? pages : [[]];
  }, [visibleLayoutItems]);
  const currentPageItems = formPages[currentPageIndex] || [];
  const shellForm = useMemo(() => {
    if (form) return form;
    if (requiresSignIn) {
      return { accessMode: 'internal' };
    }
    return null;
  }, [form, requiresSignIn]);
  const formTheme = useMemo(() => buildFormTheme(form), [form]);
  const canonicalFieldsById = useMemo(
    () => new Map((Array.isArray(form?.sheetHeaders) ? form.sheetHeaders : [])
      .map((field) => [field?.id, field])
      .filter(([fieldId]) => fieldId)),
    [form?.sheetHeaders]
  );
  const targetPrefillRowOptions = useMemo(
    () => targetPrefillRows.map((row, index) => {
      const values = row?.resolvedValues && typeof row.resolvedValues === 'object'
        ? row.resolvedValues
        : (row?.values || {});
      const displayData = sourceSheetLabelHeader
        ? getRowSelectorDisplayData(sourceSheetLabelHeader, values)
        : { label: '', imageUrl: '' };
      return {
        id: row.id,
        label: displayData.label || `Row ${index + 1}`,
        imageUrl: displayData.imageUrl,
        isProfile: `${sourceSheetLabelHeader?.type || ''}`.trim() === 'profile',
        row
      };
    }),
    [sourceSheetLabelHeader, targetPrefillRows]
  );
  const selectedTargetPrefillRow = useMemo(
    () => targetPrefillRowOptions.find((option) => option.id === selectedTargetPrefillRowId)?.row || null,
    [selectedTargetPrefillRowId, targetPrefillRowOptions]
  );
  const selectedTargetPrefillOption = useMemo(
    () => targetPrefillRowOptions.find((option) => option.id === selectedTargetPrefillRowId) || null,
    [selectedTargetPrefillRowId, targetPrefillRowOptions]
  );

  useEffect(() => {
    if (!isTargetPrefillPickerOpen) return undefined;

    const handlePointerDown = (event) => {
      if (targetPrefillPickerRef.current && !targetPrefillPickerRef.current.contains(event.target)) {
        setIsTargetPrefillPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isTargetPrefillPickerOpen]);

  useEffect(() => {
    if (!form?.id) {
      setActionPrefillDraft(null);
      setActionPrefillFieldMappings([]);
      setSourceSheetLabelHeader(null);
      setTargetPrefillRows([]);
      setSelectedTargetPrefillRowId('');
      setIsTargetPrefillPickerOpen(false);
      return;
    }

    let isActive = true;

    const hydrateActionContext = async () => {
      try {
        const targetSheetId = `${form?.linkedSheetId || form?.id || ''}`.trim();
        let resolvedSourceSheet = null;
        let resolvedActionConfig = null;

        if (formPrefillSelectorConfig.enabled && formPrefillSelectorConfig.sourceSheetId && formPrefillSelectorConfig.actionId) {
          const sourceSheet = await sheetService.getSheetById(formPrefillSelectorConfig.sourceSheetId);
          const matchingAction = (Array.isArray(sourceSheet?.actionForms) ? sourceSheet.actionForms : [])
            .find((actionForm) => (
              `${actionForm?.id || ''}`.trim() === formPrefillSelectorConfig.actionId
              && `${actionForm?.targetSheetId || ''}`.trim() === targetSheetId
              && `${actionForm?.mode || ''}`.trim() === 'linked_form'
              && actionForm?.allowTargetRowSelection === true
            ));

          if (matchingAction) {
            resolvedSourceSheet = sourceSheet;
            resolvedActionConfig = matchingAction;
          }
        }

        if (!resolvedSourceSheet && actionContext.actionId && actionContext.sourceSheetId) {
          const sourceSheet = await sheetService.getSheetById(actionContext.sourceSheetId);
          const matchingAction = (Array.isArray(sourceSheet?.actionForms) ? sourceSheet.actionForms : [])
            .find((actionForm) => (
              `${actionForm?.id || ''}`.trim() === actionContext.actionId
              && `${actionForm?.targetSheetId || ''}`.trim() === targetSheetId
              && actionForm?.mode === 'linked_form'
              && actionForm?.allowTargetRowSelection === true
            ));

          if (matchingAction) {
            resolvedSourceSheet = sourceSheet;
            resolvedActionConfig = matchingAction;
          }
        }

        if (!resolvedSourceSheet && form?.workspaceId && !formPrefillSelectorConfig.enabled) {
          const workspaceSheets = await sheetService.getSheetsByWorkspaceIds([form.workspaceId]);
          const candidate = (Array.isArray(workspaceSheets) ? workspaceSheets : []).find((sheetItem) => (
            `${sheetItem?.id || ''}`.trim() !== targetSheetId
            && (Array.isArray(sheetItem?.actionForms) ? sheetItem.actionForms : []).some((actionForm) => (
              `${actionForm?.targetSheetId || ''}`.trim() === targetSheetId
              && `${actionForm?.mode || ''}`.trim() === 'linked_form'
              && actionForm?.allowTargetRowSelection === true
            ))
          ));

          if (candidate) {
            resolvedSourceSheet = candidate;
            resolvedActionConfig = (Array.isArray(candidate.actionForms) ? candidate.actionForms : []).find((actionForm) => (
              `${actionForm?.targetSheetId || ''}`.trim() === targetSheetId
              && `${actionForm?.mode || ''}`.trim() === 'linked_form'
              && actionForm?.allowTargetRowSelection === true
            )) || null;
          }
        }

        if (!resolvedSourceSheet || !resolvedActionConfig) {
          if (!isActive) return;
          setActionPrefillDraft(null);
          setActionPrefillFieldMappings([]);
          setSourceSheetLabelHeader(null);
          setTargetPrefillRows([]);
          setSelectedTargetPrefillRowId('');
          setIsTargetPrefillPickerOpen(false);
          return;
        }

        const sourceRows = await sheetService.getSheetRows(resolvedSourceSheet.id, 500);
        if (!isActive) return;

        const nextSourceLabelHeader = Array.isArray(resolvedSourceSheet?.headers)
          ? resolvedSourceSheet.headers.find((header) => header?.isRowLabel) || null
          : null;

        const baseDraft = sheetFormService.createInitialValues(form);
        const nextActionPrefillDraft = buildActionPrefilledDraft(form, baseDraft, {});
        setActionPrefillDraft(nextActionPrefillDraft);
        setActionPrefillFieldMappings(Array.isArray(resolvedActionConfig?.fieldMappings) ? resolvedActionConfig.fieldMappings : []);
        setSourceSheetLabelHeader(nextSourceLabelHeader);
        setDraft(nextActionPrefillDraft);
        setCurrentPageIndex(0);
        setCurrentDetailRowIndex(0);
        setTargetPrefillRows(resolvedActionConfig.allowTargetRowSelection === true && normalizeSubmissionMode(form?.submissionMode) === 'singleRow'
          ? (Array.isArray(sourceRows) ? sourceRows : [])
          : []);
        setSelectedTargetPrefillRowId('');
        setIsTargetPrefillPickerOpen(false);
      } catch (error) {
        if (!isActive) return;
        setActionPrefillDraft(null);
        setActionPrefillFieldMappings([]);
        setSourceSheetLabelHeader(null);
        setTargetPrefillRows([]);
        setSelectedTargetPrefillRowId('');
        setIsTargetPrefillPickerOpen(false);
      }
    };

    hydrateActionContext();

    return () => {
      isActive = false;
    };
  }, [actionContext.actionId, actionContext.sourceSheetId, form, formPrefillSelectorConfig, user?.uid]);

  useEffect(() => {
    if (!form?.id) return;
    if (!actionPrefillDraft) return;

    if (!selectedTargetPrefillRow) {
      setDraft(actionPrefillDraft);
      return;
    }

    setDraft(buildActionPrefilledDraft(
      form,
      actionPrefillDraft,
      mapSourceRowToPrefillValues(actionPrefillFieldMappings, selectedTargetPrefillRow.values || {})
    ));
    setCurrentPageIndex(0);
    setCurrentDetailRowIndex(0);
  }, [actionPrefillDraft, actionPrefillFieldMappings, form, selectedTargetPrefillRow]);

  const refreshOptionSources = useCallback(async () => {
    const currentSheetId = `${form?.linkedSheetId || form?.id || ''}`.trim();
    const referencedSheetIds = Array.from(new Set(
      [
        currentSheetId,
        ...([...(Array.isArray(form?.fields) ? form.fields : []), ...(Array.isArray(form?.repeatableFields) ? form.repeatableFields : [])]
          .flatMap((field) => {
            const baseSheetId = `${field?.optionSource?.sheetId || ''}`.trim();
            const conditionalSheetIds = (Array.isArray(field?.conditionalOptions) ? field.conditionalOptions : [])
              .map((rule) => `${rule?.optionSource?.sheetId || ''}`.trim())
              .filter(Boolean);
            return [baseSheetId, ...conditionalSheetIds].filter(Boolean);
          })),
        ...((Array.isArray(form?.sheetHeaders) ? form.sheetHeaders : [])
          .flatMap((header) => {
            const directFormulaSheetId = `${header?.formulaConfig?.lookupSheetId || header?.formulaConfig?.filterListSheetId || ''}`.trim();
            const conditionalFormulaSheetIds = (Array.isArray(header?.conditionalFormulaRules) ? header.conditionalFormulaRules : [])
              .map((rule) => `${rule?.formulaConfig?.lookupSheetId || rule?.formulaConfig?.filterListSheetId || ''}`.trim())
              .filter(Boolean);
            return [directFormulaSheetId, ...conditionalFormulaSheetIds].filter(Boolean);
          }))
      ].filter(Boolean)
    ));

    if (!referencedSheetIds.length) {
      setOptionSourceRowsBySheetId({});
      return;
    }

    const results = await Promise.allSettled(
      referencedSheetIds.map(async (sheetId) => {
        const timeoutPromise = new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error(`Timed out loading option source ${sheetId}`)), OPTION_SOURCE_LOAD_TIMEOUT_MS);
        });
        const sourcePromise = Promise.all([
          sheetService.getSheetById(sheetId),
          sheetService.getSheetRows(sheetId, 500)
        ]);
        const [sourceSheet, rows] = await Promise.race([sourcePromise, timeoutPromise]);
        const isCurrentSheet = currentSheetId && sheetId === currentSheetId;
        return [sheetId, {
          rows,
          headers: isCurrentSheet
            ? (Array.isArray(form?.sheetHeaders) ? form.sheetHeaders : (sourceSheet?.headers || []))
            : (sourceSheet?.headers || [])
        }];
      })
    );

    const nextRowsBySheetId = results.reduce((acc, result) => {
      if (result.status !== 'fulfilled') return acc;
      const [sheetId, sourceData] = result.value;
      acc[sheetId] = sourceData;
      return acc;
    }, {});

    setOptionSourceRowsBySheetId(nextRowsBySheetId);
  }, [form]);

  useEffect(() => {
    if (!form) {
      setOptionSourceRowsBySheetId({});
      return undefined;
    }

    let isActive = true;

    const loadOptionSources = async () => {
      await refreshOptionSources();
      if (!isActive) return;
    };

    loadOptionSources();

    return () => {
      isActive = false;
    };
  }, [form, refreshOptionSources]);

  useEffect(() => {
    setCurrentPageIndex((prev) => Math.min(prev, Math.max(formPages.length - 1, 0)));
  }, [formPages.length]);

  useEffect(() => {
    setCurrentPageIndex(0);
    setCurrentDetailRowIndex(0);
  }, [id, form?.id]);

  useEffect(() => {
    if (!form?.id) return;

    const title = `${form?.title || form?.linkedSheetTitle || 'Untitled Form'}`.trim() || 'Untitled Form';
    const description = `${form?.description || ''}`.trim()
      || `Submit a response to ${title} on ParrotSheets.`;

    applySheetPageMetadata({
      title,
      description,
      metaTitle: `${title} | ParrotSheets Form`,
      metaDescription: description,
      imageUrl: `${form?.ogImageUrl || ''}`.trim(),
      themeColor: formTheme.primary,
      url: window.location.href
    });
  }, [form, formTheme.primary]);

  useEffect(() => {
    if (!form?.id || submissionMode !== 'modular' || form.allowDrafts !== true || !user?.uid || !draftHydrated || submissionState) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setDraftStatus('Saving draft...');
        await sheetFormService.saveSheetFormDraft(
          form.id,
          draft,
          {
            currentPageIndex,
            currentDetailRowIndex
          },
          user
        );
        setDraftStatus('Draft saved');
      } catch (err) {
        setDraftStatus('Draft save failed');
      }
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [
    currentDetailRowIndex,
    currentPageIndex,
    draft,
    draftHydrated,
    form,
    submissionMode,
    submissionState,
    user
  ]);

  const handleValueChange = (fieldId, nextValue, rowIndex = null) => {
    setDraft((prev) => {
      if (submissionMode !== 'modular' || rowIndex === null || rowIndex === undefined) {
        if (submissionMode === 'modular') {
          const currentSharedValues = getSharedDraftValues(prev);
          if (currentSharedValues[fieldId] === nextValue) return prev;
          return {
            ...prev,
            shared: {
              ...currentSharedValues,
              [fieldId]: nextValue
            }
          };
        }

        if (prev?.[fieldId] === nextValue) return prev;
        return { ...prev, [fieldId]: nextValue };
      }

      const currentRows = getRepeatableDraftRows(prev, form);
      if (currentRows[rowIndex]?.[fieldId] === nextValue) return prev;
      const nextRows = currentRows.map((row, index) => (
        index === rowIndex ? { ...row, [fieldId]: nextValue } : row
      ));

      return {
        ...prev,
        rows: nextRows
      };
    });
  };

  const handleMultiValueDraftChange = (fieldId, nextValue, rowIndex = null) => {
    const draftKey = rowIndex === null || rowIndex === undefined ? fieldId : `${rowIndex}_${fieldId}`;
    setMultiValueDrafts((prev) => ({ ...prev, [draftKey]: nextValue }));
  };

  const shouldHideOuterFieldLabel = useCallback((field) => {
    if (!field?.id) return false;
    const canonicalField = canonicalFieldsById.get(field.id);
    const resolvedField = canonicalField ? { ...canonicalField, ...field } : field;
    const fieldInputMode = normalizeFormInputMode(
      resolvedField?.formInputMode || getLegacyInputModeFromFieldType(resolvedField?.type)
    );
    return fieldInputMode !== 'default' && canUseSensorInputMode(resolvedField?.type);
  }, [canonicalFieldsById]);

  const handleAddMultiValues = (fieldId, currentValues = [], rowIndex = null) => {
    const draftKey = rowIndex === null || rowIndex === undefined ? fieldId : `${rowIndex}_${fieldId}`;
    const nextValues = parseMultiValueInput(multiValueDrafts[draftKey]);
    if (!nextValues.length) return;
    const normalizedCurrent = Array.isArray(currentValues) ? currentValues : [];
    const valuesToAdd = nextValues.filter((item) => !normalizedCurrent.includes(item));
    handleValueChange(fieldId, valuesToAdd.length ? [...normalizedCurrent, ...valuesToAdd] : normalizedCurrent, rowIndex);
    setMultiValueDrafts((prev) => ({ ...prev, [draftKey]: '' }));
  };

  const handleAddListItem = (fieldId, currentValues = [], rowIndex = null) => {
    const draftKey = rowIndex === null || rowIndex === undefined ? fieldId : `${rowIndex}_${fieldId}`;
    const nextItem = `${multiValueDrafts[draftKey] || ''}`.trim();
    if (!nextItem) return;
    const normalizedCurrent = Array.isArray(currentValues) ? currentValues : [];
    handleValueChange(fieldId, [...normalizedCurrent, nextItem], rowIndex);
    setMultiValueDrafts((prev) => ({ ...prev, [draftKey]: '' }));
  };

  const handleAddRepeatableRow = () => {
    setDraft((prev) => ({
      ...(prev && typeof prev === 'object' ? prev : {}),
      shared: getSharedDraftValues(prev),
      rows: [...getRepeatableDraftRows(prev, form), createEmptyRepeatableRow(form)]
    }));
  };

  const handleRemoveRepeatableRow = (rowIndex) => {
    setDraft((prev) => {
      const currentRows = getRepeatableDraftRows(prev, form);
      if (currentRows.length <= 1) {
        return {
          ...(prev && typeof prev === 'object' ? prev : {}),
          shared: getSharedDraftValues(prev),
          rows: [createEmptyRepeatableRow(form)]
        };
      }

      return {
        ...(prev && typeof prev === 'object' ? prev : {}),
        shared: getSharedDraftValues(prev),
        rows: currentRows.filter((_, index) => index !== rowIndex)
      };
    });
  };

  const handleAssetUpload = async (field, file, assetType, rowIndex = null) => {
    if (!field?.id || !file) return;
    if (!assetUploadOwnerSegment) {
      setIsLoginModalOpen(true);
      toast.error(`Sign in to upload ${assetType === 'image' ? 'images' : 'documents'}.`);
      return;
    }

    const uploadKey = `${field.id}_${assetType}`;

    try {
      setUploadingFieldKey(uploadKey);
      const safeName = `${Date.now()}_${file.name || assetType}`.replace(/[^\w.-]+/g, '_');
      const storageRef = ref(storage, `sheets/${form?.linkedSheetId}/${assetUploadOwnerSegment}/${assetType === 'image' ? 'images' : 'documents'}/${safeName}`);
      const { url } = await uploadFileAndGetUrl(storageRef, file);
      handleValueChange(field.id, url, rowIndex);
      toast.success(`${assetType === 'image' ? 'Image' : 'Document'} uploaded.`);
    } catch (err) {
      toast.error(err?.message || `Failed to upload ${assetType}.`);
    } finally {
      setUploadingFieldKey((current) => (current === uploadKey ? '' : current));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form) return;
    if (form.accessMode === 'internal' && !user) {
      setIsLoginModalOpen(true);
      return;
    }

    const missingField = visibleFields.find((field) => field.required && isFieldEmpty(field, sharedDraftValues[field.id]));
    if (missingField) {
      toast.error(`${missingField.label} is required.`);
      return;
    }

    if (submissionMode === 'modular') {
    const missingRepeatable = repeatableDraftRows
      .map((rowValues, rowIndex) => ({
          rowIndex,
          field: repeatableFields
            .filter((field) => !evaluateHeaderConditionalState(
              field,
              getResolvedFormContextValues(form, { ...sharedDraftValues, ...rowValues }, optionSourceRowsBySheetId),
              formReferenceHeaders
            ).hidden)
            .find((field) => field.required && isFieldEmpty(field, rowValues[field.id]))
        }))
      .find((entry) => entry.field);

      if (missingRepeatable?.field) {
        toast.error(`Row ${missingRepeatable.rowIndex + 1}: ${missingRepeatable.field.label} is required.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await sheetFormService.submitSheetForm(form.id, draft, user);
      await refreshOptionSources();
      if (targetPrefillRowOptions.length > 0) {
        const refreshedRows = await sheetService.getSheetRows(form.linkedSheetId || form.id, 500).catch(() => null);
        if (Array.isArray(refreshedRows)) {
          setTargetPrefillRows(refreshedRows);
        }
      }
      if (form.allowDrafts === true && user?.uid) {
        await sheetFormService.deleteSheetFormDraft(form.id, user.uid).catch(() => null);
      }
      setSubmissionState({
        title: form.successTitle || 'Submission received',
        message: form.successMessage || 'Thanks. Your response has been captured.'
      });
      setDraft(actionPrefillDraft || sheetFormService.createInitialValues(form));
      setCurrentDetailRowIndex(0);
      setSelectedTargetPrefillRowId('');
      setDraftStatus('');
      toast.success('Form submitted.');
    } catch (err) {
      toast.error(err?.message || 'Failed to submit form.');
    } finally {
      setSubmitting(false);
    }
  };

  const getVisibleRepeatableFieldsForRow = (rowValues = {}) => (
    repeatableFields.filter((field) => !evaluateHeaderConditionalState(
      field,
      getResolvedFormContextValues(form, { ...sharedDraftValues, ...rowValues }, optionSourceRowsBySheetId),
      formReferenceHeaders
    ).hidden)
  );

  const validateRepeatableRow = (rowIndex) => {
    const rowValues = repeatableDraftRows[rowIndex] || {};
    const missingField = getVisibleRepeatableFieldsForRow(rowValues)
      .find((field) => field.required && isFieldEmpty(field, rowValues[field.id]));

    if (missingField) {
      toast.error(`Row ${rowIndex + 1}: ${missingField.label} is required.`);
      return false;
    }

    return true;
  };

  const handleAddAnotherDetailRow = () => {
    if (!validateRepeatableRow(currentDetailRowIndex)) return;

    const nextIndex = repeatableDraftRows.length;
    setDraft((prev) => ({
      ...(prev && typeof prev === 'object' ? prev : {}),
      shared: getSharedDraftValues(prev),
      rows: [...getRepeatableDraftRows(prev, form), createEmptyRepeatableRow(form)]
    }));
    setCurrentDetailRowIndex(nextIndex);
  };

  const handleDoneWithDetailRows = () => {
    if (!validateRepeatableRow(currentDetailRowIndex)) return;
    handleSubmit({ preventDefault: () => {} });
  };

  const handleSaveDraftNow = async () => {
    if (!form?.id || form.allowDrafts !== true) return;
    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }

    try {
      setDraftStatus('Saving draft...');
      await sheetFormService.saveSheetFormDraft(
        form.id,
        draft,
        {
          currentPageIndex,
          currentDetailRowIndex
        },
        user
      );
      setDraftStatus('Draft saved');
      toast.success('Draft saved.');
    } catch (err) {
      setDraftStatus('Draft save failed');
      toast.error(err?.message || 'Failed to save draft.');
    }
  };

  const handleNextPage = () => {
    const missingField = currentPageItems
      .filter((item) => item.type === 'field')
      .map((item) => item.field)
      .find((field) => field.required && isFieldEmpty(field, sharedDraftValues[field.id]));

    if (missingField) {
      toast.error(`${missingField.label} is required.`);
      return;
    }

    setCurrentPageIndex((prev) => Math.min(prev + 1, formPages.length - 1));
  };


  const renderField = (field, options = {}) => {
    const { rowIndex = null, rowValues = null, contextValues: providedContextValues = null } = options;
    const canonicalField = canonicalFieldsById.get(field?.id);
    const resolvedField = canonicalField ? { ...canonicalField, ...field } : field;
    const contextValues = providedContextValues || (
      rowIndex === null
        ? resolvedSharedDraftValues
        : getResolvedFormContextValues(form, { ...sharedDraftValues, ...(rowValues || {}) }, optionSourceRowsBySheetId)
    );
    const state = evaluateHeaderConditionalState(resolvedField, contextValues, formReferenceHeaders);
    const value = rowIndex === null ? sharedDraftValues[field.id] : rowValues?.[field.id];
    const readOnly = state.readOnly;
    const displayLabel = resolveHeaderLabel(resolvedField, formReferenceHeaders, contextValues) || resolvedField.label || field.label || 'Field';
    const getResolvedOptions = () => resolveHeaderOptions(resolvedField, optionSourceRowsBySheetId, contextValues, formReferenceHeaders);
    const fieldInputMode = normalizeFormInputMode(resolvedField.formInputMode || getLegacyInputModeFromFieldType(resolvedField.type));

    if (resolvedField.type === 'checkbox') {
      return (
        <label className="flex min-h-[52px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={readOnly}
            onChange={(e) => handleValueChange(field.id, e.target.checked, rowIndex)}
            className="h-4 w-4 rounded border-slate-300"
            style={{ accentColor: formTheme.primary }}
          />
          <span className="text-sm text-slate-700">Yes</span>
        </label>
      );
    }

    if (resolvedField.type === 'textarea') {
      return (
        <textarea
          rows={5}
          value={value ?? ''}
          readOnly={readOnly}
          onChange={(e) => handleValueChange(field.id, e.target.value, rowIndex)}
          className="w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 outline-none read-only:bg-slate-100"
          style={{ borderColor: formTheme.primary }}
        />
      );
    }

    if (resolvedField.type === 'select') {
      const resolvedOptions = getResolvedOptions();
      return (
        <select
          value={`${value ?? ''}`}
          disabled={readOnly}
          onChange={(e) => handleValueChange(field.id, e.target.value, rowIndex)}
          className="w-full rounded-lg border-b border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-900 outline-none disabled:bg-slate-100"
          style={{ borderColor: value ? formTheme.primaryBorder : undefined }}
        >
          <option value="">Select...</option>
          {resolvedOptions.map((option) => (
            <option key={`${field.id}_${option}`} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (resolvedField.type === 'buttons' || resolvedField.type === 'yesno' || resolvedField.type === 'rog') {
      const resolvedOptions = getResolvedOptions();
      return (
        <div className="flex flex-wrap gap-2">
          {resolvedOptions.map((option) => {
            const presentation = getChoiceButtonPresentation({
              selected: `${value ?? ''}` === option,
              headerType: resolvedField.type,
              option,
              theme: formTheme
            });
            return (
              <button
                key={`${field.id}_${option}`}
                type="button"
                disabled={readOnly}
                onClick={() => handleValueChange(field.id, option, rowIndex)}
                className={presentation.className}
                style={presentation.style}
              >
                {option}
              </button>
            );
          })}
        </div>
      );
    }

    if (resolvedField.type === 'multiselect') {
      const resolvedOptions = getResolvedOptions();
      const selectedValues = Array.isArray(value) ? value : [];
      const draftKey = rowIndex === null || rowIndex === undefined ? field.id : `${rowIndex}_${field.id}`;
      return (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-100 p-4">
          {!readOnly && (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={multiValueDrafts[draftKey] || ''}
                  onChange={(e) => handleMultiValueDraftChange(field.id, e.target.value, rowIndex)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMultiValues(field.id, selectedValues, rowIndex);
                    }
                  }}
                  placeholder="Add value or paste comma-separated values"
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition"
                  style={{ borderColor: formTheme.primaryBorder }}
                />
                <button
                  type="button"
                  onClick={() => handleAddMultiValues(field.id, selectedValues, rowIndex)}
                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold transition"
                  style={{ borderColor: formTheme.primaryBorder, color: formTheme.primary }}
                >
                  Add
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Spaces stay inside a value. Use commas or new lines only when you want to split into multiple selections.
              </p>
            </>
          )}
          {resolvedOptions.map((option) => (
            <label key={`${field.id}_${option}`} className="flex items-center gap-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                disabled={readOnly}
                onChange={(e) => handleValueChange(
                  field.id,
                  e.target.checked
                    ? [...selectedValues, option]
                    : selectedValues.filter((item) => item !== option),
                  rowIndex
                )}
                className="h-4 w-4 rounded border-slate-300"
                style={{ accentColor: formTheme.primary }}
              />
              <span>{option}</span>
            </label>
          ))}
          {selectedValues.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {selectedValues.map((item) => (
                <span key={`${field.id}_${item}`} className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700">
                  {item}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleValueChange(field.id, selectedValues.filter((candidate) => candidate !== item), rowIndex)}
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-rose-600"
                      aria-label={`Remove ${item}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (resolvedField.type === 'list') {
      const selectedValues = Array.isArray(value) ? value : [];
      const draftKey = rowIndex === null || rowIndex === undefined ? field.id : `${rowIndex}_${field.id}`;
      return (
        <div className="space-y-3 rounded-lg border border-slate-100 bg-white p-3" style={{ borderColor: formTheme.primaryBorder }}>
          {!readOnly && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={multiValueDrafts[draftKey] || ''}
                onChange={(e) => handleMultiValueDraftChange(field.id, e.target.value, rowIndex)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddListItem(field.id, selectedValues, rowIndex);
                  }
                }}
                placeholder="Enter one item"
                className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition"
                style={{ borderColor: formTheme.primaryBorder }}
              />
              <button
                type="button"
                onClick={() => handleAddListItem(field.id, selectedValues, rowIndex)}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold transition"
                style={{ borderColor: formTheme.primaryBorder, color: formTheme.primary }}
              >
                <Plus className="h-4 w-4" />
                Add
              </button>
            </div>
          )}

          <div className="flex min-h-[36px] flex-wrap gap-2">
            {selectedValues.length ? selectedValues.map((item, index) => (
              <span key={`${field.id}_list_${index}`} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                {item}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleValueChange(field.id, selectedValues.filter((_, itemIndex) => itemIndex !== index), rowIndex)}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 transition hover:bg-white hover:text-rose-600"
                    aria-label={`Remove ${item}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            )) : (
              <span className="text-sm text-slate-400">{readOnly ? 'No items' : 'Add the first item'}</span>
            )}
          </div>
        </div>
      );
    }

    if (resolvedField.type === 'rating') {
      const current = Number(value || 0);
      return (
        <div className="flex items-center gap-2">
          {Array.from({ length: 5 }).map((_, index) => {
            const rating = index + 1;
            return (
              <button
                key={`${field.id}_star_${rating}`}
                type="button"
                disabled={readOnly}
                onClick={() => handleValueChange(field.id, rating, rowIndex)}
                className={`rounded-full p-1 ${current >= rating ? 'text-amber-400' : 'text-slate-300'}`}
              >
                <Star className="h-6 w-6 fill-current" />
              </button>
            );
          })}
        </div>
      );
    }

    if (fieldInputMode !== 'default' && canUseSensorInputMode(resolvedField.type)) {
      return (
        <SmartSensorField
          type={getSensorFieldType(fieldInputMode)}
          fieldKey={field.id}
          value={`${value ?? ''}`}
          label={<MarkdownLabel>{displayLabel}</MarkdownLabel>}
          placeholder={
            `${resolvedField.placeholder || ''}`.trim()
            || (fieldInputMode === 'barcode'
              ? 'Scan barcode or type value...'
              : fieldInputMode === 'qrcode'
                ? 'Scan QR code or type value...'
                : 'Tap NFC tag or type value...')
          }
          textSizeClass="text-sm"
          alignClass="text-left"
          onValueChange={(nextValue) => handleValueChange(field.id, nextValue, rowIndex)}
          readOnly={readOnly}
        />
      );
    }

    if (resolvedField.type === 'image' || resolvedField.type === 'document') {
      const assetUrl = `${value ?? ''}`.trim();
      const assetType = resolvedField.type;
      const uploadKey = `${field.id}_${assetType}`;
      const isUploading = uploadingFieldKey === uploadKey;

      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          {!assetUrl && !readOnly && (
            <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
              isUploading
                ? 'border-slate-200 bg-slate-100 text-slate-400'
                : 'border-slate-200 bg-white text-slate-700'
            }`}>
              <input
                type="file"
                accept={assetType === 'image'
                  ? 'image/*'
                  : '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv'}
                className="hidden"
                disabled={isUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleAssetUpload(resolvedField, file, assetType, rowIndex);
                  }
                  e.target.value = '';
                }}
              />
              <Upload className="h-4 w-4" />
              <span>{isUploading ? 'Uploading...' : `Upload ${assetType}`}</span>
            </label>
          )}

          {assetUrl ? (
            <div className="flex flex-wrap items-center gap-3">
              {assetType === 'image' ? (
                <img
                  src={assetUrl}
                  alt={field.label || 'Uploaded image'}
                  className="h-24 w-24 rounded-xl border border-slate-200 object-cover"
                />
              ) : (
                <a
                  href={assetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition"
                  style={{ color: formTheme.primary }}
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Open document</span>
                </a>
              )}

              {assetType === 'image' && (
                <a
                  href={assetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition"
                  style={{ color: formTheme.primary }}
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Open image</span>
                </a>
              )}

              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleValueChange(field.id, '', rowIndex)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                  aria-label={`Remove ${assetType}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : null}

          {!assetUrl && readOnly ? (
            <span className="text-sm text-slate-400">No {assetType} uploaded</span>
          ) : null}

          {!user && !readOnly && !canUsePublicAssetUploads ? (
            <p className="mt-3 text-xs text-slate-500">
              File uploads currently require sign-in.
            </p>
          ) : null}
        </div>
      );
    }

    if (resolvedField.type === 'date' || resolvedField.type === 'time' || resolvedField.type === 'datetime') {
      return (
        <input
          type={getInputTypeForHeader(resolvedField.type)}
          value={value ?? ''}
          readOnly={readOnly}
          onChange={(e) => handleValueChange(field.id, e.target.value, rowIndex)}
          className="w-full rounded-lg border border-slate-100 bg-slate-100 px-4 py-3 text-sm text-slate-900 outline-none read-only:bg-slate-100"
          style={{ borderColor: formTheme.primaryBorder }}
        />
      );
    }

    if (resolvedField.type === 'percent') {
      return (
        <div className="relative">
          <input
            type="number"
            value={value ?? ''}
            readOnly={readOnly}
            onChange={(e) => handleValueChange(field.id, e.target.value, rowIndex)}
            inputMode="decimal"
            className="w-full rounded-lg border border-slate-100 bg-slate-100 py-3 pl-4 pr-10 text-sm text-slate-900 outline-none read-only:bg-slate-100"
            style={{ borderColor: formTheme.primaryBorder }}
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
        </div>
      );
    }

    return (
      <input
        type={getInputTypeForHeader(resolvedField.type)}
        value={value ?? ''}
        readOnly={readOnly}
        onChange={(e) => handleValueChange(field.id, e.target.value, rowIndex)}
        inputMode={getInputModeForHeader(resolvedField.type)}
        className="w-full rounded-lg border border-slate-100 bg-slate-100 px-4 py-3 text-sm text-slate-900 outline-none read-only:bg-slate-100"
        style={{ borderColor: formTheme.primaryBorder }}
      />
    );
  };

  if (loading || authLoading) {
    return (
      <FormShell form={shellForm} theme={formTheme} user={user} userProfile={userProfile} onSignIn={() => setIsLoginModalOpen(true)} onShare={handleShare}>
        <div className="rounded-[12px] border border-slate-200 bg-slate-100 px-6 py-10 text-sm text-slate-500 shadow-sm">
          Loading form...
        </div>
        {isLoginModalOpen && (
          <LoginModal
            onClose={() => setIsLoginModalOpen(false)}
            onSuccess={() => setIsLoginModalOpen(false)}
            logo="sheets"
          />
        )}
      </FormShell>
    );
  }

  if (error) {
    return (
      <FormShell form={shellForm} theme={formTheme} user={user} userProfile={userProfile} onSignIn={() => setIsLoginModalOpen(true)} onShare={handleShare}>
        <div className="rounded-[12px] border border-slate-200 bg-white px-6 py-10 shadow-sm">
          <div className="flex items-center gap-3 text-slate-900">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Form access</h1>
              <p className="text-sm text-slate-500">{error}</p>
            </div>
          </div>
          {showErrorSignIn && !user && (
            <button
              type="button"
              onClick={() => setIsLoginModalOpen(true)}
              className="mt-6 rounded-full px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: formTheme.primary }}
            >
              Sign in to continue
            </button>
          )}
        </div>
        {isLoginModalOpen && (
          <LoginModal
            onClose={() => setIsLoginModalOpen(false)}
            onSuccess={() => setIsLoginModalOpen(false)}
            logo="sheets"
          />
        )}
      </FormShell>
    );
  }

  return (
    <FormShell form={shellForm} theme={formTheme} user={user} userProfile={userProfile} onSignIn={() => setIsLoginModalOpen(true)} onShare={handleShare}>
      {form?.showFormIntro !== false && (
        <div className="rounded-[12px] bg-[#f6f5f5] px-3 py-4 shadow-md backdrop-blur" style={{ border: `1px solid ${formTheme.primary}` }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: formTheme.primary }}>ParrotSheets Form</p>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">{form?.title || 'Untitled form'}</h1>
          
          {form?.description && (
            <p className="mt-3 text-sm leading-6 text-slate-600">{form.description}</p>
          )}
          <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold text-slate-600" style={{ backgroundColor: formTheme.primarySoft }}>
            <span>{form?.accessMode === 'internal' ? 'Internal' : 'Public'}</span>
            {form?.linkedSheetTitle ? <span>• {form.linkedSheetTitle}</span> : null}
          </div>
        </div>
      )}

      <div className="mt-4">
        {submissionState ? (
          <div className="rounded-[12px] border border-emerald-100 bg-slate-100 px-3 py-8 shadow-md">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="mt-5 text-2xl font-black text-slate-900">{submissionState.title}</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">{submissionState.message}</p>
            <button
              type="button"
              onClick={() => setSubmissionState(null)}
              className="mt-6 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
            >
              Submit another response
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-[12px] border border-slate-200 bg-white/50 px-3 py-6 shadow-sm sm:px-6 sm:py-8">
            {targetPrefillRowOptions.length > 0 && (
              <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <label className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: formTheme.primary }}>
                      Prefill From Row
                    </label>
                    <p className="mt-1 text-sm text-slate-600">
                      Leave this blank to use only the action mappings, or choose a source-sheet row to copy its values into this new-row form.
                    </p>
                    <div className="relative mt-3" ref={targetPrefillPickerRef}>
                      <button
                        type="button"
                        onClick={() => setIsTargetPrefillPickerOpen((prev) => !prev)}
                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-900 outline-none transition hover:border-slate-300 focus:border-[#008080]"
                        aria-expanded={isTargetPrefillPickerOpen}
                        aria-haspopup="listbox"
                      >
                        <span className="min-w-0 flex-1">
                          {!selectedTargetPrefillOption ? (
                            <span className="block truncate text-slate-500">Use action prefills only</span>
                          ) : selectedTargetPrefillOption.isProfile ? (
                            <span className="flex items-center gap-3">
                              {selectedTargetPrefillOption.imageUrl ? (
                                <img
                                  src={selectedTargetPrefillOption.imageUrl}
                                  alt=""
                                  className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200"
                                />
                              ) : (
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                                  {selectedTargetPrefillOption.label.slice(0, 1).toUpperCase() || '?'}
                                </span>
                              )}
                              <span className="min-w-0 truncate font-medium text-slate-900">
                                {selectedTargetPrefillOption.label}
                              </span>
                            </span>
                          ) : (
                            <span className="block truncate">{selectedTargetPrefillOption.label}</span>
                          )}
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${isTargetPrefillPickerOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isTargetPrefillPickerOpen && (
                        <div className="absolute left-0 right-0 z-20 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTargetPrefillRowId('');
                              setIsTargetPrefillPickerOpen(false);
                            }}
                            className={`flex w-full items-center rounded-xl px-3 py-3 text-left text-sm transition ${
                              !selectedTargetPrefillRowId ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50'
                            }`}
                            role="option"
                            aria-selected={!selectedTargetPrefillRowId}
                          >
                            Use action prefills only
                          </button>
                          {targetPrefillRowOptions.map((option) => (
                            <button
                              key={`target_prefill_row_${option.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedTargetPrefillRowId(option.id);
                                setIsTargetPrefillPickerOpen(false);
                              }}
                              className={`mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm transition ${
                                selectedTargetPrefillRowId === option.id ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50'
                              }`}
                              role="option"
                              aria-selected={selectedTargetPrefillRowId === option.id}
                            >
                              {option.isProfile ? (
                                <>
                                  {option.imageUrl ? (
                                    <img
                                      src={option.imageUrl}
                                      alt=""
                                      className="h-10 w-10 shrink-0 rounded-md object-cover ring-1 ring-slate-200"
                                    />
                                  ) : (
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                                      {option.label.slice(0, 1).toUpperCase() || '?'}
                                    </span>
                                  )}
                                  <span className="min-w-0 truncate font-medium">{option.label}</span>
                                </>
                              ) : (
                                <span className="truncate">{option.label}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedTargetPrefillRowId && (
                    <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold text-slate-700" style={{ backgroundColor: formTheme.primarySoft }}>
                      Copying row values
                    </span>
                  )}
                </div>
              </div>
            )}
            {submissionMode === 'modular' && form?.allowDrafts === true && (
              <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: formTheme.primary }}>Drafts</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {user
                        ? 'Your progress can be saved and resumed later while signed in.'
                        : 'Draft saving is enabled for this form. Sign in to save and resume later.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {draftStatus ? (
                      <span className="text-xs font-semibold text-slate-500">{draftStatus}</span>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleSaveDraftNow}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                      style={{ borderColor: formTheme.primaryBorder, color: formTheme.primary }}
                    >
                      {user ? 'Save progress' : 'Sign in to save'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {formPages.length > 1 && (
              <div className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: formTheme.primary }}>Form Page</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">Page {currentPageIndex + 1} of {formPages.length}</p>
                </div>
                <div className="flex items-center gap-2">
                  {formPages.map((_, pageIndex) => (
                    <span
                      key={`form_page_indicator_${pageIndex + 1}`}
                      className={`h-2.5 w-2.5 rounded-full ${pageIndex === currentPageIndex ? '' : 'bg-slate-200'}`}
                      style={pageIndex === currentPageIndex ? { backgroundColor: formTheme.primary } : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-6">
              {currentPageItems.map((item) => {
                if (item.type === 'sectionDivider') {
                  return (
                    <div key={item.id} className="md:col-span-6">
                      <div className="mb-1 inline-flex px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 [white-space:break-spaces]">
                        {item.label}
                      </div>
                      <div className="border-t border-slate-400" />
                    </div>
                  );
                }

                if (item.type === 'textBlock') {
                  return (
                    <div key={item.id} className="md:col-span-6">
                      <FormRichTextBlock item={item} theme={formTheme} />
                    </div>
                  );
                }

                if (item.type === 'imageSlide') {
                  return (
                    <div key={item.id} className="md:col-span-6">
                      <FormImageSlide item={item} theme={formTheme} />
                    </div>
                  );
                }

                const field = item.field;
                const hideOuterFieldLabel = shouldHideOuterFieldLabel(field);
                return (
                  <div key={item.id} className={getFormFieldWidthClass(item.width)}>
                    {!hideOuterFieldLabel ? (
                      <div className="mb-4 flex items-center gap-2">
                        <label className="text-sm font-normal text-slate-800">
                          <MarkdownLabel>{field.label}</MarkdownLabel>
                        </label>
                        {field.required && (
                          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-rose-600">
                            Required
                          </span>
                        )}
                      </div>
                    ) : field.required ? (
                      <div className="mb-2 flex items-center">
                        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-rose-600">
                          Required
                        </span>
                      </div>
                    ) : null}
                    {renderField(field)}
                  </div>
                );
              })}
            </div>

            {submissionMode === 'modular' && repeatableFields.length > 0 && currentPageIndex === formPages.length - 1 && (
              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: formTheme.primary }}>Detail Module</p>
                    <p className="mt-1 text-sm text-slate-600">Each completed detail entry will be saved as a separate ParrotSheets row.</p>
                  </div>
                </div>

                {(repeatableEntryMode === 'allAtOnce' ? repeatableDraftRows : [repeatableDraftRows[currentDetailRowIndex] || createEmptyRepeatableRow(form)]).map((rowValues, index) => {
                  const rowIndex = repeatableEntryMode === 'allAtOnce' ? index : currentDetailRowIndex;
                  const repeatableContextValues = getResolvedFormContextValues(
                    form,
                    { ...sharedDraftValues, ...rowValues },
                    optionSourceRowsBySheetId
                  );
                  const visibleRepeatableLayoutItems = repeatableLayoutItems.filter((item) => (
                    item.type !== 'field' || !evaluateHeaderConditionalState(item.field, repeatableContextValues, formReferenceHeaders).hidden
                  )).map((item) => item.type === 'field'
                    ? {
                        ...item,
                        field: {
                          ...item.field,
                          label: resolveHeaderLabel(item.field, formReferenceHeaders, repeatableContextValues)
                        }
                      }
                    : item);
                  return (
                  <div key={`repeatable_row_${rowIndex + 1}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Entry {rowIndex + 1}</p>
                        <p className="mt-1 text-xs text-slate-500">Enter the detail values for this entry.</p>
                      </div>
                      {(repeatableEntryMode === 'allAtOnce' || repeatableDraftRows.length > 1) && (
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveRepeatableRow(rowIndex);
                            if (repeatableEntryMode === 'stepper') {
                              setCurrentDetailRowIndex((prev) => Math.max(0, Math.min(prev - (rowIndex === prev ? 1 : 0), repeatableDraftRows.length - 2)));
                            }
                          }}
                          className="inline-flex items-center justify-center rounded-full border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid gap-5 md:grid-cols-6">
                      {visibleRepeatableLayoutItems.map((item) => {
                        if (item.type === 'sectionDivider') {
                          return (
                            <div key={`repeatable_${rowIndex}_${item.id}`} className="md:col-span-6">
                              <div className="mb-3 inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm [white-space:break-spaces]">
                                {item.label}
                              </div>
                              <div className="border-t border-slate-200" />
                            </div>
                          );
                        }

                        if (item.type === 'textBlock') {
                          return (
                            <div key={`repeatable_${rowIndex}_${item.id}`} className="md:col-span-6">
                              <FormRichTextBlock item={item} theme={formTheme} />
                            </div>
                          );
                        }

                        if (item.type === 'imageSlide') {
                          return (
                            <div key={`repeatable_${rowIndex}_${item.id}`} className="md:col-span-6">
                              <FormImageSlide item={item} theme={formTheme} />
                            </div>
                          );
                        }

                        if (item.type === 'pageBreak') {
                          return (
                            <div key={`repeatable_${rowIndex}_${item.id}`} className="md:col-span-6 border-t border-dashed border-slate-300 pt-2" />
                          );
                        }

                        const field = item.field;
                        const hideOuterFieldLabel = shouldHideOuterFieldLabel(field);
                        return (
                          <div key={`repeatable_${rowIndex}_${field.id}`} className={getFormFieldWidthClass(item.width || field.formWidth)}>
                            {!hideOuterFieldLabel ? (
                              <div className="mb-2 flex items-center gap-2">
                                <label className="text-sm font-semibold text-slate-800">
                                  <MarkdownLabel>{field.label}</MarkdownLabel>
                                </label>
                                {field.required && (
                                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-rose-600">
                                    Required
                                  </span>
                                )}
                              </div>
                            ) : field.required ? (
                              <div className="mb-2 flex items-center">
                                <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-rose-600">
                                  Required
                                </span>
                              </div>
                            ) : null}
                            {renderField(field, { rowIndex, rowValues, contextValues: repeatableContextValues })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )})}

                {repeatableEntryMode === 'stepper' && (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                    <p className="text-sm font-semibold text-slate-800">
                      {form?.repeatableAddPrompt || 'Add another row?'}
                    </p>
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={handleAddAnotherDetailRow}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                      >
                        {form?.repeatableAddLabel || 'Add another row'}
                      </button>
                      <button
                        type="button"
                        onClick={handleDoneWithDetailRows}
                        className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                        style={{ backgroundColor: formTheme.primary }}
                      >
                        {form?.repeatableDoneLabel || 'Continue'}
                      </button>
                    </div>
                  </div>
                )}
                {repeatableEntryMode === 'allAtOnce' && (
                  <button
                    type="button"
                    onClick={handleAddRepeatableRow}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                  >
                    {form?.repeatableAddLabel || 'Add another row'}
                  </button>
                )}
              </div>
            )}

            {form?.accessMode === 'internal' && !user && (
              <button
                type="button"
                onClick={() => setIsLoginModalOpen(true)}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
              >
                <Lock className="h-4 w-4" />
                Sign in to submit
              </button>
            )}

            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  if (submissionMode === 'modular' && currentPageIndex === formPages.length - 1 && repeatableEntryMode === 'stepper' && currentDetailRowIndex > 0) {
                    setCurrentDetailRowIndex((prev) => Math.max(prev - 1, 0));
                    return;
                  }
                  setCurrentPageIndex((prev) => Math.max(prev - 1, 0));
                }}
                disabled={currentPageIndex === 0 && currentDetailRowIndex === 0}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>

              {currentPageIndex < formPages.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNextPage}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
                  style={{ backgroundColor: formTheme.primary }}
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : submissionMode === 'modular' && repeatableFields.length > 0 && repeatableEntryMode === 'stepper' ? (
                <span className="text-xs text-slate-400">Use the detail module actions to continue.</span>
              ) : (
                <button
                  type="submit"
                  disabled={submitting || (form?.accessMode === 'internal' && !user)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300"
                  style={{ backgroundColor: submitting || (form?.accessMode === 'internal' && !user) ? undefined : formTheme.primary }}
                >
                  <Send className="h-4 w-4" />
                  {submitting ? 'Submitting...' : (form?.submitLabel || 'Submit')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      <div className="rounded-[12px] border border-slate-200 bg-white px-4 py-3 text-center shadow-sm">
        <p className="text-xs text-slate-700">
          Submit only information you intend to share through this form.
        </p>
        <p className="mt-2 text-xs text-slate-700">
          If anything looks unexpected, contact the form owner before continuing.
        </p>
        <a
          href="https://parrotsheets.com"
          target="_blank"
          rel="noreferrer"
          className="inline-flex justify-center"
        >
          <img src={sheetLogo} alt="ParrotSheets" className="mx-auto mt-5 h-10 w-auto object-contain grayscale contrast-125 opacity-80" />
        </a>
      </div>

      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onSuccess={() => setIsLoginModalOpen(false)}
          logo="sheets"
        />
      )}
    </FormShell>
  );
};

export default SheetFormPage;
